import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, FileText, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { permitTypes } from '../mock';

const Services = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-pink-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-4">
            <h1 className="text-5xl font-bold text-gray-900">
              2C - Conseils & <span className="text-pink-600">Conceptions</span>
            </h1>
            <video src="https://customer-assets.emergentagent.com/job_7bec0238-1e03-4c93-afc1-e7baf1238116/artifacts/nk3b9zei_logo-U2C.mp4" autoPlay loop muted playsInline className="h-24 w-auto flex-shrink-0 hidden sm:block rounded-lg" />
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            Découvrez mes services d'accompagnement pour vos autorisations d'urbanisme
          </p>
          <div className="mb-4" style={{animation: 'pulseScale 2s ease-in-out infinite'}}>
            <Link to="/contact" className="inline-block px-8 py-3 bg-pink-600 hover:bg-pink-700 text-white text-xl font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300">
              Etude de faisabilité offerte pour votre projet !!
            </Link>
          </div>
          <style>{`@keyframes pulseScale { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }`}</style>
        </div>
      </section>

      {/* Plans 2D-3D Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Réalisation de plans 2D-3D
            </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Afin de pouvoir vous projeter plus rapidement dans votre futur projet, je réalise des plans 2D et des modèles 3D détaillés ainsi que des séquences vidéo pour une meilleure immersion.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                    <span className="text-pink-600 text-sm font-bold">✓</span>
                  </div>
                  <span className="text-gray-700">Plans techniques précis et conformes aux normes</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                    <span className="text-pink-600 text-sm font-bold">✓</span>
                  </div>
                  <span className="text-gray-700">Modélisation 3D réaliste de votre projet</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                    <span className="text-pink-600 text-sm font-bold">✓</span>
                  </div>
                  <span className="text-gray-700">Insertion paysagère dans l'environnement existant</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                    <span className="text-pink-600 text-sm font-bold">✓</span>
                  </div>
                  <span className="text-gray-700">Visualisation avant/après pour mieux décider</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                    <span className="text-pink-600 text-sm font-bold">✓</span>
                  </div>
                  <span className="text-gray-700">Restructuration et aménagement intérieur</span>
                </li>
                <li className="flex items-start">
                  <div className="w-6 h-6 bg-pink-100 rounded-full flex items-center justify-center mt-1 mr-3 flex-shrink-0">
                    <span className="text-pink-600 text-sm font-bold">✓</span>
                  </div>
                  <span className="text-gray-700">Plans techniques : électricité, plomberie, cloisonnement, etc...</span>
                </li>
              </ul>
          </div>
        </div>
      </section>

      {/* Permit Types Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">
              Les différentes demandes d'autorisation
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Je vous accompagne sur tous types d'autorisations d'urbanisme
            </p>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-3">
              Vous pouvez retrouver toutes les fiches pratiques des différentes autorisations sur le lien suivant :
            </p>
            <div className="mt-6">
              <a
                href="https://www.service-public.gouv.fr/particuliers/vosdroits/N319"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-pink-600 hover:bg-pink-700 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                data-testid="link-service-public"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Lien vers les Autorisations d'urbanisme - Service public.gouv
              </a>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {permitTypes.map((permit) =>
            <Card key={permit.id} className="overflow-hidden border-2 border-gray-200 hover:border-pink-300 hover:shadow-2xl transition-all duration-300 group">
                <div className="relative h-48 overflow-hidden">
                  <img
                  src={permit.image}
                  alt={permit.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 to-transparent" />
                  <h3 className="absolute bottom-4 left-4 right-4 text-xl font-bold text-white">
                    {permit.title}
                  </h3>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {permit.description}
                  </p>
                  
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Caractéristiques :</h4>
                    <ul className="space-y-2">
                      {permit.characteristics.map((char, idx) =>
                    <li key={idx} className="flex items-start text-sm">
                          <span className="text-pink-600 mr-2 mt-0.5">•</span>
                          <span className="">{char}</span>
                        </li>
                    )}
                    </ul>
                  </div>

                  <Button
                  asChild
                  className="w-full bg-pink-600 hover:bg-pink-700 text-white">

                    <a
                    href={permit.cerfaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center">

                      <FileText className="w-4 h-4 mr-2" />
                      Informations et formulaire CERFA
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">
            Besoin d'aide pour votre projet ?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Contactez-moi pour un accompagnement personnalisé et un devis gratuit
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-pink-600 hover:bg-pink-700 text-white shadow-xl">
              <a href="tel:0761926962">
                Appelez-moi : 07.61.92.69.62
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900">
              <Link to="/contact">
                Envoyer un message
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>);

};

export default Services;